import Pspace from "../assets/CSC2429.pdf";

export interface LinkItem {
    label: string;
    href: string;
}

export interface ResearchItem {
    title: string;
    authors: string;
    venue: string;
    year: string;
    links: LinkItem[];
}

export interface Note {
    title: string;
    date: string;
    description: string;
    links: LinkItem[];
}

export interface Review {
    title: string;
    author: string;
    date: string;
    href: string;
}

export const research: ResearchItem[] = [
    {
        title: "Aurora: A Leverage-Aware Spectral Optimizer",
        authors: "Alec Dewulf, Dhruv Pai, Li Yang, Ashley Zhang, and Ben Keigwin",
        venue: "arXiv preprint",
        year: "2026",
        links: [
            { label: "arXiv", href: "https://arxiv.org/abs/2606.27715" },
            { label: "Tilde", href: "https://tilderesearch.com/research" },
            { label: "code", href: "https://github.com/tilde-research/aurora-release" },
        ],
    },
    {
        title: "Online KL Shampoo",
        authors: "Ashley Zhang, Ben Keigwin, Dhruv Pai, and Alec Dewulf",
        venue: "Tilde Research technical release",
        year: "2026",
        links: [
            { label: "Tilde", href: "https://tilderesearch.com/research" },
            { label: "code", href: "https://github.com/tilde-research/online-kl-shampoo-release" },
        ],
    },
    {
        title: "DataGuard: Guaranteeing Private Training in Systolic-array Based Accelerators",
        authors: "Pawan Kumar Sanjaya, Christina Giannoula, Nikhil Shreekumar, Ian Colbert, Alec Dewulf, Mehdi Saeedi, Ihab Amer, Gabor Sines, and Nandita Vijaykumar",
        venue: "arXiv preprint",
        year: "2026",
        links: [
            { label: "arXiv", href: "https://arxiv.org/abs/2606.16809" },
        ],
    },
    {
        title: "Parallax: Parameterized Local Linear Attention for Language Modeling",
        authors: "Yifei Zuo, Dhruv Pai, Zhichen Zeng, Alec Dewulf, Shuming Hu, and Zhaoran Wang",
        venue: "arXiv preprint",
        year: "2026",
        links: [
            { label: "arXiv", href: "https://arxiv.org/abs/2605.29157" },
            { label: "code", href: "https://github.com/yifei-zuo/Parallax" },
            { label: "Tilde", href: "https://tilderesearch.com/research" },
        ],
    },
    {
        title: "Towards Compositional Steepest Descent",
        authors: "Ben Keigwin, Li Yang, Dhruv Pai, Ashley Zhang, and Alec Dewulf",
        venue: "Tilde Research technical release",
        year: "2026",
        links: [
            { label: "Tilde", href: "https://tilderesearch.com/research" },
            { label: "code", href: "https://github.com/tilde-research/comp-muon-release" },
        ],
    },
    {
        title: "Wall Attention: Length Generalization With Diagonal Gates",
        authors: "Dhruv Pai, Timor Averbuch, Ashley Zhang, Ben Keigwin, and Alec Dewulf",
        venue: "Tilde Research technical release",
        year: "2026",
        links: [
            { label: "Tilde", href: "https://tilderesearch.com/research" },
            { label: "code", href: "https://github.com/fla-org/flash-linear-attention" },
        ],
    },
    {
        title: "Nitrobrew: Fast, Lossless Distillation for Free",
        authors: "Dhruv Pai, Timor Averbuch, Alec Dewulf, Ben Keigwin, and Ashley Zhang",
        venue: "Tilde Research technical release",
        year: "2026",
        links: [
            { label: "Tilde", href: "https://tilderesearch.com/research" },
            { label: "code", href: "https://github.com/tilde-research/nitrobrew-release" },
        ],
    },
    {
        title: "Complexity and Transformers",
        authors: "Alec Dewulf",
        venue: "Tilde Research vignette",
        year: "2025",
        links: [
            { label: "Tilde", href: "https://tilderesearch.com/research" },
        ],
    },
    {
        title: "Priming effects in inefficient visual search: Real, but transient",
        authors: "Jeremy M. Wolfe, Sneha B. Suresh, Alec W. Dewulf, and Wanyi Lyu",
        venue: "Attention, Perception, & Psychophysics",
        year: "2022",
        links: [
            { label: "journal", href: "https://doi.org/10.3758/s13414-022-02503-5" },
        ],
    },
];

export const notes: Note[] = [
    {
        title: "The Nondeterministic Time Hierarchy",
        date: "April 26, 2024",
        description: "Notes on the nondeterministic time hierarchy theorem.",
        links: [{ label: "Read", href: "/blog/nondeterministic-time-hierarchy" }],
    },
    {
        title: "A Downward Self-Reducible, Random Self-Reducible PSPACE-Complete Problem",
        date: "February 27, 2024",
        description: "A writeup on a PSPACE-complete problem that is downward and random self-reducible.",
        links: [{ label: "PDF", href: Pspace }],
    },
];

export const reviews: Review[] = [
    { title: "The Sirens of Titan", author: "Kurt Vonnegut", date: "April 5, 2025", href: "/sirens" },
    { title: "The Sun Also Rises", author: "Ernest Hemingway", date: "May 19, 2024", href: "/sun" },
    { title: "On the Road", author: "Jack Kerouac", date: "May 3, 2024", href: "/road" },
    { title: "The Adventures of Huckleberry Finn", author: "Mark Twain", date: "March 29, 2024", href: "/finn" },
    { title: "The Prince", author: "Niccolò Machiavelli", date: "March 17, 2024", href: "/prince" },
    { title: "Cat's Cradle", author: "Kurt Vonnegut", date: "March 14, 2024", href: "/cradle" },
    { title: "Jane Eyre", author: "Charlotte Brontë", date: "March 10, 2024", href: "/eyre" },
    { title: "A Shining", author: "Jon Fosse", date: "December 25, 2023", href: "/shining" },
    { title: "The Sea", author: "John Banville", date: "December 25, 2023", href: "/thesea" },
    { title: "The Hitchhiker's Guide to the Galaxy", author: "Douglas Adams", date: "September 9, 2023", href: "/hitch" },
    { title: "War and Peace", author: "Leo Tolstoy", date: "June 25, 2023", href: "/warpeace" },
    { title: "Brave New World", author: "Aldous Huxley", date: "May 5, 2023", href: "/brave" },
    { title: "East of Eden", author: "John Steinbeck", date: "January 19, 2023", href: "/eden" },
    { title: "How Not to Be Wrong", author: "Jordan Ellenberg", date: "October 6, 2022", href: "/howWrong" },
    { title: "The Old Man and the Sea", author: "Ernest Hemingway", date: "August 17, 2021", href: "/sea" },
];
